import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppInformationService, AuthService, GenesisUnitOfWork, OrderService, ReportService, SessionInformationService } from 'app/core/core.module';
import { Confirmation, ConfirmationPaymentMethod, Order, OrderType } from 'app/core/model/entity-model';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import _ from 'lodash';
import moment from 'moment';

@Component({
    selector: 'app-confirmation-overview',
    templateUrl: './confirmation-overview.component.html',
    styleUrls: ['./confirmation-overview.component.scss'],
    standalone: false
})
export class ConfirmationOverviewComponent implements OnInit {
    orderService = inject(OrderService);

    confirmations: any;
    confirmationTypes: any[];
    orderTypes: OrderType[];
    confirmationPaymentMethods: ConfirmationPaymentMethod[];
    filter: any;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    constructor(
        private uow: GenesisUnitOfWork,
        public auth: AuthService,
        private router: Router,
        private appInfo: AppInformationService,
        private sessionInfo: SessionInformationService,
        private reportService: ReportService
    ) {}

    async ngOnInit() {
        const filter = this.sessionInfo.getItem('confirmation-overview-filter');
        this.filter = filter ?? { showAll: false };

        this.confirmationTypes = this.uow.lookups.confirmationTypes;
        this.orderTypes = this.uow.lookups.orderTypes;
        this.confirmationPaymentMethods = (await this.uow.confirmationPaymentMethods.whereActive()) as ConfirmationPaymentMethod[];
        await this.getData();
    }

    async getData() {
        const confirmations = (await this.uow.getConfirmations('order, dealer, aches', this.filter.showAll)) as Confirmation[];
        confirmations.forEach((confirmation: any) => {
            confirmation.hasPendingAches = confirmation.aches.some((ach) => !ach.deleted && !ach.approved && !ach.declined);
            confirmation.hasPendingBankDeposit = confirmation.aches.some((ach) => !ach.deleted && ach.approved && !ach.bankDepositAcknowledged);
            confirmation.hasPendingBankDeposit = confirmation.aches.some((ach) => !ach.deleted && ach.approved && !ach.bankDepositAcknowledged);
            confirmation.dealerUnverifiedNorthpoint = confirmation.dealer.northpointCINVerified === null;
            confirmation.dealerUnverifiedRedOak = confirmation.dealer.redOakCINVerified === null;
        });
        this.confirmations = confirmations as Confirmation[];
    }

    async toggleShowAll() {
        this.dataGrid.instance.clearSelection();
        this.filter.showAll = !this.filter.showAll;
        await this.getData();
        this.sessionInfo.setItem('confirmation-overview-filter', this.filter);
    }

    // this method is used to clear the selection when the user searches for something
    onOptionChanged(e) {
        if (e.fullName === 'searchPanel.text') {
            e.component.__searchChanged = true;
        }
        const filterCheck = _.includes(e.fullName, 'filterValue');
        if (filterCheck) {
            this.dataGrid.instance.clearSelection();
        }
    }

    // this method is used to clear the selection when the user searches for something
    onContentReady(e) {
        if (e.component.__searchChanged) {
            //__searchChanged is for the search box
            e.component.__searchChanged = false;
            this.dataGrid.instance.clearSelection();
        }
    }

    async rowPrepare(e) {
        if (e.rowType === 'data') {
            if (!e.data.approved && !e.data.declined) {
                e.rowElement.className = e.rowElement.className + ' waiting-on-dealer-order';
            }

            const confirmationPaymentMethod = this.confirmationPaymentMethods.find((x) => x.id === e.data.confirmationPaymentMethodId);

            if (e.data.approved && !e.data.acknowledged) {
                if (confirmationPaymentMethod && confirmationPaymentMethod.requiresAch) {
                    // ach information needs to be filled out before the confirmation can be acknowledged - both SalesOrder and Winter
                    if (e.data.hasPendingAches) {
                        e.rowElement.className = e.rowElement.className + ' waiting-on-dealer-ach';
                    }
                    // winter orders need to have the bank deposit acknowledged before the confirmation can be acknowledged
                    else if (e.data.order.typeId === Order.Types.Winter && e.data.hasPendingBankDeposit) {
                        e.rowElement.className = e.rowElement.className + ' waiting-on-ach-deposit';
                    } else {
                        e.rowElement.className = e.rowElement.className + ' approved-to-acknowledge';
                    }
                } else if (e.data.confirmationPaymentMethodId === ConfirmationPaymentMethod.Methods.Northpoint) {
                    if (e.data.dealerUnverifiedNorthpoint) {
                        e.rowElement.className = e.rowElement.className + ' waiting-on-ach-deposit';
                    } else {
                        e.rowElement.className = e.rowElement.className + ' approved-to-acknowledge';
                    }
                } else if (e.data.confirmationPaymentMethodId === ConfirmationPaymentMethod.Methods.RedOak) {
                    if (e.data.dealerUnverifiedRedOak) {
                        e.rowElement.className = e.rowElement.className + ' waiting-on-ach-deposit';
                    } else {
                        e.rowElement.className = e.rowElement.className + ' approved-to-acknowledge';
                    }
                }else {
                    e.rowElement.className = e.rowElement.className + ' approved-to-acknowledge';
                }
                if (e.data.order.typeId === Order.Types.Winter) {
                    e.rowElement.className = e.rowElement.className + ' approved-winter-to-acknowledge';
                }
            }
            if (e.data.declined && !e.data.acknowledged) {
                if (e.data.cancelReason === 'I no longer wish to place an order') {
                    e.rowElement.className = e.rowElement.className + ' declined-to-acknowledge';
                }
                if (e.data.cancelReason === 'I wish to make changes to my order' || e.data.cancelReason === 'Delivery Method Changed - New Confirmation Required') {
                    e.rowElement.className = e.rowElement.className + ' declined-requesting-changes';
                }
            }
        }
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            }
        );
    }

    selectionChange(e) {}

    async rowDoubleClick(e) {
        if (!e.data || !e.data.id) return;
    }

    async open(orderId: number) {
        if (!orderId) return;
        this.router.navigate([`/sales-orders/${orderId}`]);
    }

    async viewConfirmation(code: any) {
        if (!code) return;
        this.router.navigate([`/portal/dealer-confirmation/${code}`]);
    }

    async resetLink(confirmations: Confirmation[]) {
        if (!(await confirm(`Are you sure you want to reset the selected confirmation link${confirmations.length > 1 ? 's' : ''}?`, `${this.appInfo.settings.company}`))) return;

        this.appInfo.pleaseWaitShow();
        confirmations.forEach((confirmation) => {
            confirmation.created = moment(new Date()).toDate();
            confirmation.createdBy = this.auth.currentUser.userName;
            confirmation.viewed = null;
        });
        await this.uow.commit();
        this.appInfo.pleaseWaitHide();
        this.dataGrid.instance.clearSelection();
    }

    async acknowledge() {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;

        if (selectedRow.order.typeId === Order.Types.Winter && !selectedRow.declined) {
            const result = await confirm(`This is a Winter Order. Before Acknowledging, verify you have received payment. Continue?`, this.appInfo.settings.company);
            if (!result) return;
        }

        selectedRow.acknowledged = new Date();
        selectedRow.acknowledgedBy = this.auth.currentUser.userName;
        await this.uow.commit();
        if (selectedRow.declined) await this.getData();
        else this.router.navigate(['./sales-orders/' + selectedRow.orderId]);
    }

    async delete() {
        const confirmation = this.dataGrid.instance.getSelectedRowsData()[0] as Confirmation;
        if (!confirmation) return;

        const canDelete = this.orderService.canDeleteConfirmation(confirmation);
        if (!canDelete) {
            let message = '';
            if (!this.orderService.isConfirmationOpen(confirmation)) message = 'Confirmations that have been approved/declined cannot be deleted.';
            else if (this.orderService.confirmationHasClosedAches(confirmation)) message = 'Confirmations with closed ACHs cannot be deleted.';
            await alert(message, this.appInfo.settings.company);
            return;
        }

        const result = await confirm(`Are you sure you want to delete the selected confirmation? All related ACHs will be deleted.`, this.appInfo.settings.company);
        if (!result) return;

        await this.appInfo.pleaseWaitShow();
        this.orderService.deleteConfirmations([confirmation]);
        await this.uow.commit();
        await this.getData();
        this.dataGrid.instance.clearSelection();
        await this.appInfo.pleaseWaitHide();
    }

    print(confirmation: Confirmation) {
        if (!confirmation) return;
        this.reportService.printConfirmation(confirmation.code);
    }
}
